import Overlay from 'react-bootstrap/Overlay';

import styles from 'css/src/components/common/ui/Popover.module.scss';

import React from 'react';

export enum POPOVER_PLACEMENT {
    TOP = 'top',
    BOTTOM = 'bottom',
    LEFT = 'left',
    RIGHT = 'right',
    TOP_START = 'top-start',
    TOP_END = 'top-end',
    BOTTOM_START = 'bottom-start',
    BOTTOM_END = 'bottom-end',
    LEFT_START = 'left-start',
    LEFT_END = 'left-end',
    RIGHT_START = 'right-start',
    RIGHT_END = 'right-end',
}

type PopoverProps = {
    target: React.RefObject<HTMLElement | null> | HTMLElement | null;
    placement: POPOVER_PLACEMENT;
    show: boolean;
    children: React.ReactNode;
    rootClose?: boolean;
    onHide?: () => void;
    showArrow?: boolean;
    offset?: [number, number];
    flip?: boolean;
};

// This component will be the base component for overlay/popover functionality
// It will be extended to support existing popover/overlay functionality and we will refactor over time
// https://lumen5.atlassian.net/browse/IO-1504
function Popover({
    // The target element is where the popover is positioned relative to
    target,
    // Where the popover is positioned relative to the target
    placement,
    // Visibility of popover
    show,
    // React node of the popover content
    children,
    // Specifies whether the popover should trigger `onHide` when clicking away
    // Requires onHide prop to work
    rootClose = true,
    // Callback fired when clicking away
    onHide,
    showArrow = false,
    offset,
    // Flip to the opposite side of the target when there is not enough room
    // in the viewport for the requested placement
    flip = false,
}: PopoverProps) {
    const popperConfig = offset
        ? { modifiers: [{ name: 'offset', options: { offset } }] }
        : undefined;
    return (
        <Overlay
            target={target}
            placement={placement}
            show={show}
            rootClose={rootClose}
            onHide={onHide}
            flip={flip}
            popperConfig={popperConfig}
        >
            {({
                arrowProps,
                placement: activePlacement,
                show: _show,
                hasDoneInitialMeasure: _hasDoneInitialMeasure,
                popper: _popper,
                ...props
            }) => {
                const [primaryPlacement] = (activePlacement ?? '').split('-');
                return (
                    <div
                        {...props}
                        className={styles['lumen5-popover']}
                        data-placement={primaryPlacement}
                    >
                        {showArrow && <div {...arrowProps} className={styles['arrow']} />}
                        {children}
                    </div>
                );
            }}
        </Overlay>
    );
}

export default Popover;
